import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
} from "react";

import { cn } from "../base/utils";

export interface AppShellSidebarClassNames {
  root?: string;
  header?: string;
  navigation?: string;
  footer?: string;
}

export interface AppShellSidebarProps extends Omit<
  ComponentPropsWithoutRef<"aside">,
  "children"
> {
  /**
   * Primary navigation content. Routing, permissions and navigation state stay
   * with the consuming application.
   */
  navigation: ReactNode;
  /** Optional content placed above the primary navigation. */
  header?: ReactNode;
  /** Optional utility area kept separate from the primary navigation. */
  footer?: ReactNode;
  /** Width override. Defaults to the component token, then 16rem. */
  width?: CSSProperties["width"];
  classNames?: AppShellSidebarClassNames;
}

const rootTokenStyles: CSSProperties = {
  width: "var(--monkeys-component-app-shell-sidebar-width, 16rem)",
  borderRadius:
    "var(--monkeys-component-app-shell-sidebar-radius, var(--radius, 0.75rem))",
  background:
    "var(--monkeys-component-app-shell-sidebar-background, hsl(var(--card)))",
  color:
    "var(--monkeys-component-app-shell-sidebar-foreground, hsl(var(--card-foreground)))",
  borderColor:
    "var(--monkeys-component-app-shell-sidebar-border-color, hsl(var(--border)))",
  boxShadow: "var(--monkeys-component-app-shell-sidebar-shadow, none)",
};

const headerTokenStyles: CSSProperties = {
  padding:
    "var(--monkeys-component-app-shell-sidebar-header-padding, var(--global-spacing, 0.75rem))",
  borderColor:
    "var(--monkeys-component-app-shell-sidebar-header-border-color, hsl(var(--border)))",
};

const navigationTokenStyles: CSSProperties & {
  "--monkeys-component-accordion-item-border-color": string;
} = {
  padding:
    "var(--monkeys-component-app-shell-sidebar-navigation-padding, var(--global-spacing, 0.75rem))",
  "--monkeys-component-accordion-item-border-color": "transparent",
};

const footerTokenStyles: CSSProperties = {
  padding:
    "var(--monkeys-component-app-shell-sidebar-footer-padding, var(--global-spacing, 0.75rem))",
  background:
    "var(--monkeys-component-app-shell-sidebar-footer-background, hsl(var(--muted) / 0.22))",
  borderColor:
    "var(--monkeys-component-app-shell-sidebar-footer-border-color, hsl(var(--border)))",
};

/**
 * Persistent, outermost application navigation surface.
 *
 * This is intentionally a single shell component rather than a composition of
 * generic sidebar primitives. It owns the surface hierarchy while applications
 * retain routing and navigation behavior inside the semantic slots.
 */
export const AppShellSidebar = forwardRef<HTMLElement, AppShellSidebarProps>(
  function AppShellSidebar(
    {
      navigation,
      header,
      footer,
      width,
      className,
      classNames,
      style,
      "aria-label": ariaLabel = "Application navigation",
      ...props
    },
    ref,
  ) {
    return (
      <aside
        ref={ref}
        aria-label={ariaLabel}
        data-app-shell-sidebar=""
        className={cn(
          "relative isolate flex h-full min-h-0 shrink-0 flex-col overflow-hidden border",
          classNames?.root,
          className,
        )}
        style={{
          ...rootTokenStyles,
          ...(width === undefined ? null : { width }),
          ...style,
        }}
        {...props}
      >
        {header ? (
          <div
            data-slot="header"
            className={cn("shrink-0 border-b", classNames?.header)}
            style={headerTokenStyles}
          >
            {header}
          </div>
        ) : null}

        <div
          data-slot="navigation"
          className={cn(
            "relative min-h-0 flex-1 overflow-hidden",
            classNames?.navigation,
          )}
          style={navigationTokenStyles}
        >
          {navigation}
        </div>

        {footer ? (
          <div
            data-slot="footer"
            className={cn("shrink-0 border-t", classNames?.footer)}
            style={footerTokenStyles}
          >
            {footer}
          </div>
        ) : null}
      </aside>
    );
  },
);

AppShellSidebar.displayName = "AppShellSidebar";
